import { NextPageContext } from 'next'
import { Iata, PlatformConfig, PlatformConfigSchema } from '@reward-platform/shared-schemas'
import {
  getValidBasePathForHost,
  getAviosBaseUrl,
  getOpCoMetaConfig,
  getPlatformConfigFromCookies,
  getWhitelabelOpCoForHost,
  isAviosHostValid,
  validateOpCoLocale,
} from '@reward-platform/platform-config'
import { getOpCoFromRequest } from '@reward-platform/platform-config/server'
import { PlatformError } from '~/utils/errors'
import { isLocal } from '~/utils/envChecks'
import { redirectResponse } from '../utils'
import {
  getCookiesForClientOrServer,
  getHostForClientOrServer,
  isServerPageContext,
} from './pageContext.utils'

export const handleRedirectToAviosPlatform = (pageContext: NextPageContext, enabled = true) => {
  const isServer = isServerPageContext(pageContext)
  if (!isServer || isLocal()) {
    return
  }

  const currentUrl = pageContext.req.url
  const nextUrl = getAviosBaseUrl()
  const shouldRedirectToAvios = enabled && !currentUrl?.includes(nextUrl)
  if (shouldRedirectToAvios) {
    redirectResponse(pageContext.res, nextUrl, 301)
  }
}

const SWITCH_FLY_URLS: Record<Iata, string> = {
  BA: '',
  EI: '',
  IB: 'https://aviosandmoney.iberia.com/apps/shopping/#/search/room',
}

export const handleRedirectToSwitchFly = (pageContext: NextPageContext, targetOpCos: Iata[]) => {
  const isServer = isServerPageContext(pageContext)
  if (!isServer || isLocal()) {
    return
  }

  const activeOpCo = getOpCoFromRequest(pageContext.req)
  const targetUrl = SWITCH_FLY_URLS[activeOpCo]

  const shouldRedirectToSwitchFly = targetOpCos.includes(activeOpCo)
  if (shouldRedirectToSwitchFly) {
    pageContext.res.setHeader(
      'Cache-Control',
      'no-store, no-cache, must-revalidate, proxy-revalidate'
    )
    redirectResponse(pageContext.res, targetUrl, 307)
  }
}

export const getPlatformBasePathFromPageContext = (
  context: NextPageContext
): string | undefined => {
  try {
    const requestHost = getHostForClientOrServer(context, true)
    return getValidBasePathForHost(requestHost)
  } catch (error) {
    if (error instanceof PlatformError) {
      error.logRequestToDatadog(context.req)
    }

    return undefined
  }
}

export const getPlatformConfigFromPageContext = (context: NextPageContext): PlatformConfig => {
  try {
    const requestHost = getHostForClientOrServer(context, true)
    const maybeWhitelabelOpCo = getWhitelabelOpCoForHost(requestHost)
    if (maybeWhitelabelOpCo) {
      const defaultMeta = getOpCoMetaConfig(maybeWhitelabelOpCo)
      return {
        opCo: maybeWhitelabelOpCo,
        locale: validateOpCoLocale(maybeWhitelabelOpCo, context.locale),
        market: defaultMeta.defaultCountryCode,
        theme: defaultMeta.whitelabelTheme,
      }
    }

    if (isAviosHostValid(requestHost)) {
      const cookies = getCookiesForClientOrServer(context)
      const cookieConfig = getPlatformConfigFromCookies(cookies)
      // until cookie strategy is set up properly on avios.com we will use the router as the source of truth
      const validContextLocale = validateOpCoLocale(cookieConfig.opCo, context.locale)
      const basePath = getPlatformBasePathFromPageContext(context)
      return PlatformConfigSchema.parse({ ...cookieConfig, locale: validContextLocale, basePath })
    }

    throw new PlatformError(`host headers were missing or invalid - host: ${requestHost}`)
  } catch (error) {
    if (error instanceof PlatformError) {
      error.logRequestToDatadog(context.req)
    }

    throw error
  }
}
